import * as React from "react";
import { useContext, useCallback, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import chunk from "lodash/chunk";
import ColorPicker from "components/common/ColorPicker";
import Value from "views/components/Value";
import ChartColorContext from "views/components/visualizations/ChartColorContext";
import Popover from "components/common/Popover";
import FieldType from "views/logic/fieldtypes/FieldType";
import { colors as defaultColors } from "views/components/visualizations/Colors";
import { EVENT_COLOR, eventsDisplayName } from "views/logic/searchtypes/events/EventHandler";
import WidgetFocusContext from "views/components/contexts/WidgetFocusContext";
import FieldTypesContext from "views/components/contexts/FieldTypesContext";
import { keySeparator, humanSeparator } from "views/Constants";
import useMapKeys from "views/components/visualizations/useMapKeys";
import { useIsInteractiveMode } from "views/components/contexts/InteractiveContext";
import WidgetRenderingContext from "views/components/widgets/WidgetRenderingContext";
const ColorHint = styled.div(
  ({ color }) => css`
    cursor: pointer;
    background-color: ${color} !important; /* Needed for report generation */
    -webkit-print-color-adjust: exact !important; /* Needed for report generation */
    width: 12px;
    height: 12px;
  `
);
const FixedContainer = styled.div`
  display: grid;
  grid-template: 4fr auto / 1fr;
  grid-template-areas: '.' '.';
  height: 100%;
`;
const VariableContainer = styled.div(
  ({ $height, $width }) => css`
    display: grid;
    width: ${$width}px;
    grid-template-columns: ${$width}px;
    grid-template-rows: ${$height}px auto;
    gap: 0;
    justify-content: center;
  `
);
const LegendContainerBase = styled.div(
  ({ theme }) => css`
    padding: ${theme.spacings.xxs};
  `
);
const LegendContainer = styled(LegendContainerBase)`
  max-height: 100px;
  overflow: auto;
`;
const Legend = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.xxs};
    width: 100%;
  `
);
const LegendRow = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: ${theme.spacings.xs};
  `
);
const LegendEntryContainer = styled.div`
  display: flex;
  align-items: center;
`;
const ValueContainer = styled.div`
  margin-left: 5px;
  line-height: 1;
`;
const defaultLabelMapper = (data) => data.map(({ name, originalName }) => originalName != null ? originalName : name);
const stringLenSort = (s1, s2) => {
  if (s1.length < s2.length) {
    return -1;
  }
  if (s1.length === s2.length) {
    return 0;
  }
  return 1;
};
const columnPivotsToFields = (config) => {
  var _a, _b;
  return (_b = (_a = config == null ? void 0 : config.columnPivots) == null ? void 0 : _a.flatMap((pivot) => pivot.fields)) != null ? _b : [];
};
const LegendEntry = ({ value, labelsWithField }) => {
  const { colors, setColor } = useContext(ChartColorContext);
  const interactive = useIsInteractiveMode();
  const mapKeys = useMapKeys();
  const [showPopover, setShowPopover] = useState(false);
  const defaultColor = value === eventsDisplayName ? EVENT_COLOR : void 0;
  const val = labelsWithField.map(
    ({ label, field, type }) => field ? /* @__PURE__ */ React.createElement(Value, { key: `${field}:${label}`, type, value: label, field }) : label
  );
  const humanLabel = Object.values(labelsWithField).map(({ label, field }) => mapKeys(label, field)).join(humanSeparator);
  const _onColorSelect = useCallback(
    (color) => {
      setColor(value, color);
      setShowPopover(false);
    },
    [setColor, value]
  );
  const togglePopover = useMemo(() => interactive ? () => setShowPopover((show) => !show) : () => {
  }, [interactive]);
  return /* @__PURE__ */ React.createElement(LegendEntryContainer, null, /* @__PURE__ */ React.createElement(Popover, { position: "top", withArrow: true, opened: showPopover }, /* @__PURE__ */ React.createElement(Popover.Target, null, /* @__PURE__ */ React.createElement(ColorHint, { "aria-label": "Color Hint", onClick: togglePopover, color: colors.get(value, defaultColor) })), /* @__PURE__ */ React.createElement(Popover.Dropdown, { title: `Configuration for ${humanLabel}` }, /* @__PURE__ */ React.createElement(ColorPicker, { color: colors.get(value, defaultColor), colors: defaultColors, onChange: _onColorSelect }))), /* @__PURE__ */ React.createElement(ValueContainer, null, val));
};
const TableCell = ({ value, fieldTypes, labelFields }) => {
  const labelsWithField = value.split(keySeparator).map((label, idx) => {
    var _a, _b, _c;
    const field = labelFields[idx];
    const fieldType = (_c = (_b = (_a = fieldTypes == null ? void 0 : fieldTypes.currentQuery) == null ? void 0 : _a.find((type) => type.name === field)) == null ? void 0 : _b.type) != null ? _c : FieldType.Unknown;
    return { label, field, type: fieldType };
  });
  return /* @__PURE__ */ React.createElement("div", { key: value }, /* @__PURE__ */ React.createElement(LegendEntry, { labelsWithField, value }));
};
const InteractiveLegend = ({ config, fieldTypes, labelFields, labels }) => {
  const _labelFields = useMemo(() => labelFields(config), [config, labelFields]);
  const tableCells = labels.sort(stringLenSort).map((value) => /* @__PURE__ */ React.createElement(TableCell, { key: value, value, labelFields: _labelFields, fieldTypes }));
  const result = chunk(tableCells, 5).map((cells, index) => (
    // eslint-disable-next-line react/no-array-index-key
    /* @__PURE__ */ React.createElement(LegendRow, { key: index }, cells)
  ));
  return /* @__PURE__ */ React.createElement(LegendContainer, null, /* @__PURE__ */ React.createElement(Legend, null, result));
};
const FlexLegendContainer = styled(LegendContainerBase)(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.spacings.xxs} ${theme.spacings.xs};
    flex-wrap: wrap;
  `
);
const NoninteractiveLegend = ({ config, fieldTypes, labels, labelFields }) => {
  const _labelFields = useMemo(() => labelFields(config), [config, labelFields]);
  return /* @__PURE__ */ React.createElement(FlexLegendContainer, null, labels.map((value) => {
    const labelsWithField = value.split(keySeparator).map((label, idx) => {
      var _a, _b, _c;
      const field = _labelFields[idx];
      const fieldType = (_c = (_b = (_a = fieldTypes == null ? void 0 : fieldTypes.currentQuery) == null ? void 0 : _a.find((type) => type.name === field)) == null ? void 0 : _b.type) != null ? _c : FieldType.Unknown;
      return { label, field, type: fieldType };
    });
    return /* @__PURE__ */ React.createElement(LegendEntry, { key: value, labelsWithField, value });
  }));
};
const PlotLegend = ({
  children,
  config,
  chartData,
  labelMapper = defaultLabelMapper,
  labelFields = columnPivotsToFields,
  neverHide = false,
  height,
  width
}) => {
  const { columnPivots, series } = config;
  const { focusedWidget } = useContext(WidgetFocusContext);
  const interactive = useIsInteractiveMode();
  const fieldTypes = useContext(FieldTypesContext);
  const { limitHeight } = useContext(WidgetRenderingContext);
  const labels = labelMapper(chartData);
  const Container = limitHeight ? FixedContainer : VariableContainer;
  if (!neverHide && !(focusedWidget == null ? void 0 : focusedWidget.editing) && series.length <= 1 && columnPivots.length <= 0) {
    return /* @__PURE__ */ React.createElement(Container, { $height: height, $width: width }, children);
  }
  const LegendComponent = interactive ? InteractiveLegend : NoninteractiveLegend;
  return /* @__PURE__ */ React.createElement(Container, { $height: height, $width: width }, children, /* @__PURE__ */ React.createElement(LegendComponent, { config, fieldTypes, labelFields, labels }));
};
export default PlotLegend;
